import React, { useEffect, useState } from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import Interaction from '../models/Interaction';
import {getInteractionById, getInteractionFileMetadata, getPresignedUrl} from '../services/interaction-service';
import DeleteInteractionModal from "../components/DeleteInteractionModal";
import FileMetadata from "../models/FileMetadata";
import FileUrlRequest from "../models/FileUrlRequest";

import imageIcon from '../assets/image-icon.png';
import textIcon from '../assets/text-icon.png';
import pdfIcon from '../assets/pdf-icon.png';
import wordIcon from '../assets/word-icon.png';
import excelIcon from '../assets/excel-icon.png';
import powerpointIcon from '../assets/powerpoint-icon.png';
import defaultIcon from '../assets/default-icon.png';
import DeleteFileModal from "../components/DeleteFileModal";
import {formatInteractionTime} from "../utils/DateTimeUtils";

const InteractionPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const [interaction, setInteraction] = useState<Interaction | null>(null);
    const [errorToast, setErrorToast] = useState<string>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(location.state?.errorMessage || '');
    const [fileMetaData, setFileMetadata] = useState<FileMetadata[] | null>(null);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);

    useEffect(() => {
        const loadInteraction = async () => {
            try {
                if (id) {
                    const response = await getInteractionById(Number(id));
                    if (response.status === 200 && response.data) {
                        setInteraction(response.data);
                    } else {
                        throw new Error('Invalid interaction response');
                    }
                } else {
                    throw new Error('No interaction id');
                }
            } catch (err: any) {
                console.error('Error loading interaction', err);
                setErrorToast('Error loading interaction. Please try again later.');
            }
        };

        loadInteraction();
    }, [id]);

    useEffect(() => {
        const fetchFileMetadata = async () => {
            try {
                if (id && interaction?.hasFiles) {
                    const response = await getInteractionFileMetadata(Number(id));
                    if (response.status === 200 && response.data) {
                        setFileMetadata(response.data);
                    } else {
                        throw new Error('Unable to retrieve file metadata: ' + response.status);
                    }
                } else setFileMetadata([]);
            } catch (err: any) {
                console.error('Error fetching file metadata', err);
                setErrorToast('Unable to load files for interaction at this time.');
            }
        }
        if(!!interaction) {
            fetchFileMetadata();
        }
    }, [id, interaction]);

    function capitalizeFirst(str: string | undefined): string {
        if (!str) return '';
        const lower = str.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    function canOpenInTab(contentType: string) {
        if (!contentType) return false;
        if (contentType === "text/plain") return true;
        if (contentType === 'application/pdf') return true;
        if (contentType.startsWith('image/')) return true;
        return false;
    }

    function getFileIcon(contentType: string) {
        if (!contentType) return defaultIcon;
        if(contentType.startsWith('image/')) return imageIcon;
        if(contentType.startsWith('text/')) return textIcon;
        if(contentType === 'application/pdf') return pdfIcon;
        if(contentType.includes('word')) return wordIcon;
        if(contentType.includes('excel') || contentType.includes('spreadsheet')) return excelIcon;
        if(contentType.includes('presentation') || contentType.includes('powerpoint')) return powerpointIcon;
        return defaultIcon;
    }

    async function handleOpenFile (file: FileMetadata) {
        const urlRequest: FileUrlRequest = {
            fileName: file.fileName,
            key: file.key,
            mode: "inline",
            contentType: file.contentType
        }
        const response = await getPresignedUrl(urlRequest);
        if (response.status === 200 && response.data) {
            window.open(response.data, '_blank', 'noopener,noreferrer');
        } else {
            console.error("Unable to open file: " + file);
            setErrorToast("Unable to open file at this time. Please try again later");
        }
    }

    async function handleDownloadFile (file: FileMetadata) {
        const urlRequest: FileUrlRequest = {
            fileName: file.fileName,
            key: file.key,
            mode: "attachment",
            contentType: file.contentType
        }
        const response = await getPresignedUrl(urlRequest);
        if (response.status === 200 && response.data) {
            window.location.assign(response.data);
        } else {
            console.error("Unable to download file: " + file);
            setErrorToast("Unable to download file at this time. Please try again later");
        }
    }

    const handleDeleteFile = (fileName: string) => {
        setFileMetadata(prev =>
            prev ? prev.filter(f => f.fileName !== fileName) : prev
        );
    }

    if (interaction === null) {
        return (
            <div data-testid='loading-placeholder' className="min-h-screen flex flex-col">
                <Navbar />
                <main className="flex-grow container mx-auto px-4 py-6 text-center">
                    Loading...
                </main>
                <Footer />
                <ErrorToast
                    data-testid="error-toast"
                    message={errorToast}
                    clearError={() => setErrorToast('')}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-6">
                <button
                    data-testid="back-btn"
                    onClick={() => navigate(`/client/${interaction.clientId}`)}
                    className="text-crumbblue hover:underline mb-4"
                >
                    &larr; Back to Client
                </button>

                <div data-testid="interaction-details" className="mb-6">
                    <h1 className="text-3xl font-bold mb-2">{interaction.subject}</h1>

                    <div data-testid="details" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p><strong>Date:</strong> {interaction.interactionDate.toLocaleDateString()}</p>
                            <p><strong>Time:</strong> {formatInteractionTime(interaction) ?? '—'}</p>
                            <p><strong>Type:</strong> {interaction.type}</p>
                            <p><strong>Outcome:</strong> {interaction.outcome}</p>
                        </div>

                        <div>
                            {interaction.duration !== 0 &&
                                <p><strong>Duration:</strong> {interaction.duration} {capitalizeFirst(interaction.durationUnit)}</p>
                            }
                            <div className="flex flex-wrap items-center mt-2">
                                <strong className="mr-2">Tags:</strong>
                                {interaction.tags && interaction.tags.map(tag => (
                                    <span
                                        key={tag}
                                        className="bg-crumbblue text-white px-2 py-1 rounded-full mr-2 mb-2"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            data-testid="edit-interaction"
                            className="px-5 py-2 rounded bg-crumbblue text-white font-medium shadow hover:shadow-lg transition"
                            onClick={() => navigate('/interaction/update/' + id)}
                        >
                            Edit Interaction
                        </button>
                        <button
                            data-testid="delete-interaction"
                            className="px-5 py-2 rounded bg-red-600 text-white font-medium shadow-sm hover:bg-red-700 transition"
                            onClick={() => setIsDeleteModalOpen(true)}
                        >
                            Delete Interaction
                        </button>
                    </div>
                    {errorMessage && (
                        <div data-testid="error-message" className="text-red-600 text-sm mt-1">
                            {errorMessage}
                        </div>
                    )}
                    <div className="mt-6">
                        <p><strong>Notes:</strong></p>
                        <p className="whitespace-pre-wrap">
                            {interaction.notes ? interaction.notes : <em>No notes provided.</em>}
                        </p>
                    </div>

                    <section
                        className="mt-6"
                        aria-labelledby="ai-summary-title"
                        data-testid="ai-summary-section"
                    >
                        <div className="mb-2">
                            <span
                                id="ai-summary-title"
                                className="inline-flex items-center rounded-md bg-crumbblue text-white text-sm font-semibold px-2 py-0.5"
                            >
                                AI Summary
                            </span>
                        </div>

                        <div className="rounded-xl border border-crumbblue/30 bg-crumbblue/5 shadow-sm p-4">
                            {interaction.aiNotesSummary && interaction.aiNotesSummary.trim() ? (
                                <p data-testid="ai-summary" className="whitespace-pre-wrap text-[15px] leading-6">
                                    {interaction.aiNotesSummary}
                                </p>
                            ) : (
                                <p
                                    data-testid="ai-summary-placeholder"
                                    className="text-gray-500 italic"
                                >
                                    No AI summary generated yet.
                                </p>
                            )}
                        </div>
                    </section>

                    {/* File previews */}
                    <div className="mt-6" data-testid="files">
                        <h2 className="text-xl font-semibold mb-2">Files</h2>

                        {/* Loading state */}
                        {fileMetaData === null && (
                            <div
                                data-testid="files-loading"
                                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                            >
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="animate-pulse h-24 rounded-lg border border-gray-200 bg-gray-100"
                                    />
                                ))}
                            </div>
                        )}

                        {/* Empty state */}
                        {fileMetaData !== null && fileMetaData.length === 0 && (
                            <div data-testid="no-files" className="border border-gray-300 rounded p-4 text-gray-500">
                                No files uploaded.
                            </div>
                        )}

                        {/* Files grid */}
                        {fileMetaData !== null && fileMetaData.length > 0 && interaction && (
                            <div
                                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4"
                                data-testid="files-grid"
                            >
                                {fileMetaData.map((f) => {
                                    const openable = canOpenInTab(f.contentType);

                                    return (
                                        <div
                                            key={f.key}
                                            className="group relative rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow transition overflow-hidden"
                                            data-testid={`${f.fileName}-tile`}
                                            title={f.fileName}
                                        >
                                            <div className="h-24 flex items-center justify-center text-4xl bg-gray-50">
                                                <img
                                                    data-testid={`${f.fileName}-icon`}
                                                    src={getFileIcon(f.contentType)}
                                                    alt={f.fileName}
                                                    className="mx-auto h-16 w-16 object-contain mb-4"
                                                />
                                            </div>
                                            <div className="px-3 py-2 text-sm truncate" data-testid="file-name">
                                                {f.fileName}
                                            </div>
                                            <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/20 transition">
                                                <div className="absolute inset-x-0 bottom-2 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition pointer-events-auto">
                                                    {openable && (
                                                        <button
                                                            type="button"
                                                            data-testid="file-open"
                                                            onClick={() => handleOpenFile(f)}
                                                            className="px-2 py-1 rounded-md bg-white text-crumbblue text-xs font-medium shadow hover:shadow-md"
                                                        >
                                                            Open
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        data-testid="file-download"
                                                        onClick={() => handleDownloadFile(f)}
                                                        className="px-2 py-1 rounded-md bg-white text-crumbblue text-xs font-medium shadow hover:shadow-md"
                                                    >
                                                        Download
                                                    </button>
                                                    <button
                                                        type="button"
                                                        data-testid={`delete-file-${f.fileName}`}
                                                        onClick={() => {
                                                            setSelectedFile(f.fileName);
                                                            setIsDeleteFileModalOpen(true);
                                                        }}
                                                        className="px-2 py-1 rounded-md bg-white text-crumbblue text-xs font-medium shadow hover:shadow-md"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </main>

            <Footer />
            { id && (
                <DeleteInteractionModal
                    isOpen={isDeleteModalOpen}
                    interactionId={Number(id)}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onDelete={() => navigate(`/client/${interaction.clientId}`)}
                    onError={(error) => setErrorToast(error)}
                />
            )}
            {id && selectedFile && (
                <DeleteFileModal
                    isOpen={isDeleteFileModalOpen}
                    interactionId={Number(id)}
                    fileName={selectedFile}
                    onClose={() =>{
                        setIsDeleteFileModalOpen(false);
                        setSelectedFile(null);
                    }}
                    onDelete={(fileName) => handleDeleteFile(fileName)}
                    onError={(error) => setErrorToast(error)} />
            )}
            <ErrorToast
                data-testid="error-toast"
                message={errorToast}
                clearError={() => setErrorToast('')}
            />
        </div>
    );
};

export default InteractionPage;